/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {defer, Observable} from 'rxjs';
import {finalize} from 'rxjs/operators';

/**
 * Tracks how many requests a component currently has in flight so a spinner stays
 * up until the last one settles. A single boolean cannot do this: when a component
 * starts several requests at once, the first one to finish clears the flag while the
 * others are still running, exposing controls that have no data in them yet.
 */
export class LoadingCounter {

  private pending = 0;

  /**
   * True while at least one tracked request is still in flight
   */
  get active(): boolean {
    return this.pending > 0;
  }

  /**
   * Counts the given request as in flight until it completes, errors or is unsubscribed.
   * T defaults to any so services that declare an untyped return keep their untyped value
   * instead of collapsing to {}.
   * @param source the request to track
   */
  track<T = any>(source: Observable<T>): Observable<T> {
    // Counting on subscribe rather than here keeps a request that is never subscribed to
    // from pinning the count above zero and leaving the spinner up for good
    return defer(() => {
      this.pending++;
      return source;
    }).pipe(finalize(() => {
      this.pending = Math.max(0, this.pending - 1);
    }));
  }

  /**
   * Counts a unit of work that is not driven by an observable, such as a file read.
   * Every call must be paired with a call to end().
   */
  begin(): void {
    this.pending++;
  }

  /**
   * Marks a unit of work started with begin() as finished
   */
  end(): void {
    this.pending = Math.max(0, this.pending - 1);
  }

  /**
   * Drops all in flight work, for cases where a component resets itself entirely
   */
  reset(): void {
    this.pending = 0;
  }
}
